import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarBlank, Clock } from "@phosphor-icons/react";

/**
 * Composite date + time picker using shadcn Calendar in a Popover.
 *
 * Props:
 *   value: Date | null
 *   onChange: (Date | null) => void
 *   testIdDate?: string
 *   testIdTime?: string
 */
export function DateTimePicker({ value, onChange, testIdDate, testIdTime }) {
  const [open, setOpen] = useState(false);
  const timeStr = value
    ? `${String(value.getHours()).padStart(2, "0")}:${String(value.getMinutes()).padStart(2, "0")}`
    : "";
  const dateLabel = value
    ? value.toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "Selecciona fecha";

  const handleDate = (d) => {
    if (!d) return;
    const merged = new Date(d);
    if (value) {
      merged.setHours(value.getHours(), value.getMinutes(), 0, 0);
    } else {
      merged.setHours(9, 0, 0, 0);
    }
    onChange(merged);
    setOpen(false);
  };

  const handleTime = (t) => {
    const [h, m] = (t || "").split(":");
    if (h == null || m == null) return;
    const base = value ? new Date(value) : new Date();
    base.setHours(Number(h), Number(m), 0, 0);
    onChange(base);
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className="block text-[11px] font-mono-data uppercase tracking-widest text-white/50 mb-2">
          Fecha
        </label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid={testIdDate}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-sm bg-[#0A0A0A] border border-white/10 text-sm text-white hover:border-white/20 focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] transition-colors outline-none"
            >
              <span className={value ? "text-white" : "text-white/40"}>{dateLabel}</span>
              <CalendarBlank size={16} className="text-white/50" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="p-0 w-auto bg-[#111111] border-white/10"
          >
            <Calendar
              mode="single"
              selected={value || undefined}
              onSelect={handleDate}
              initialFocus
              className="bg-[#111111] text-white"
              classNames={{
                day_selected:
                  "bg-[#0055FF] text-white hover:bg-[#0055FF] hover:text-white focus:bg-[#0055FF] focus:text-white",
                day_today: "bg-white/10 text-white",
                head_cell:
                  "text-white/40 rounded-sm w-8 font-mono-data font-normal text-[10px] uppercase",
                caption_label: "text-sm font-semibold text-white",
                nav_button:
                  "h-7 w-7 bg-transparent p-0 opacity-70 hover:opacity-100 hover:bg-white/10 rounded-sm border border-white/10 text-white",
                day: "h-8 w-8 p-0 font-normal text-white/80 hover:bg-white/10 rounded-sm aria-selected:opacity-100",
                day_outside: "text-white/20",
                day_disabled: "text-white/20",
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
      <div>
        <label className="block text-[11px] font-mono-data uppercase tracking-widest text-white/50 mb-2">
          Hora
        </label>
        <div className="relative">
          <Clock
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50 pointer-events-none"
          />
          <input
            data-testid={testIdTime}
            type="time"
            value={timeStr}
            onChange={(e) => handleTime(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 rounded-sm bg-[#0A0A0A] border border-white/10 text-sm text-white focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] transition-colors outline-none [color-scheme:dark]"
          />
        </div>
      </div>
    </div>
  );
}
